import { FC, useState } from 'react'
import { Card, Button, Input, InputNumber, Popconfirm, Row, Col, message } from 'antd'
import { 
  PlusOutlined, 
  DeleteOutlined,
  SaveOutlined,
  CarOutlined,
  ClockCircleOutlined,
  MoreOutlined
} from '@ant-design/icons'
import { IExpenseItem } from '@/supabase/DailySalesSummary/dailySalesSummary.interface'

// CSS for financial input styling
const financialInputStyles = `
  .financial-input .ant-input-number-input {
    text-align: right !important;
    padding-right: 10px !important;
    font-size: 16px !important;
  }
  .financial-input .ant-input-number-prefix {
    font-size: 16px !important;
  }
  .expense-text-input.ant-input {
    font-size: 16px !important;
  }
`

interface ExpenseSectionProps {
  expenseType: 'FUEL' | 'EMPLOYEE_OVERTIME' | 'OTHER'
  expenses: IExpenseItem[]
  onAddExpense: (name: string, amount: number) => Promise<boolean>
  onUpdateExpense: (expenseId: string, name: string, amount: number) => Promise<boolean>
  onDeleteExpense: (expenseId: string) => Promise<boolean>
  loading?: boolean
}

export const ExpenseSection: FC<ExpenseSectionProps> = ({
  expenseType,
  expenses,
  onAddExpense,
  onUpdateExpense,
  onDeleteExpense,
  loading = false
}) => {
  const [newExpenseName, setNewExpenseName] = useState('')
  const [newExpenseAmount, setNewExpenseAmount] = useState<number>(0)
  const [isAddingNew, setIsAddingNew] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [localChanges, setLocalChanges] = useState<Map<string, { name: string; amount: number }>>(new Map())
  const [errorMessage, setErrorMessage] = useState<string>('')

  // Get Thai display name and icon for expense type
  const getExpenseTypeInfo = (type: string) => {
    switch (type) {
      case 'FUEL':
        return { 
          name: 'เติมน้ำมัน', 
          icon: <CarOutlined className="text-blue-500" />,
          color: 'blue',
          placeholder: 'เติมน้ำมัน'
        }
      case 'EMPLOYEE_OVERTIME':
        return { 
          name: 'OT', 
          icon: <ClockCircleOutlined className="text-green-500" />,
          color: 'blue',
          placeholder: 'OT พนักงาน'
        }
      case 'OTHER':
        return { 
          name: 'อื่นๆ', 
          icon: <MoreOutlined className="text-orange-500" />,
          color: 'blue',
          placeholder: 'รายการอื่นๆ'
        }
      default:
        return { 
          name: type, 
          icon: <MoreOutlined className="text-gray-500" />,
          color: 'blue',
          placeholder: 'รายการ'
        }
    }
  }

  const typeInfo = getExpenseTypeInfo(expenseType)

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount)
  }

  // Calculate total for this expense type
  const totalAmount = expenses.reduce((sum, expense) => sum + expense.amountTHB, 0)

  // Check if there are any changes
  const hasChanges = localChanges.size > 0

  // Handle expense field change (local only)
  const handleExpenseChange = (expenseId: string, field: 'name' | 'amount', value: string | number) => {
    const originalExpense = expenses.find(e => e.id === expenseId)
    if (!originalExpense) return

    const currentLocal = localChanges.get(expenseId) || { 
      name: originalExpense.name, 
      amount: originalExpense.amountTHB 
    }
    
    const updatedLocal = {
      ...currentLocal,
      [field]: value
    }
    
    // Check if this change is different from original
    const isDifferent = updatedLocal.name !== originalExpense.name || updatedLocal.amount !== originalExpense.amountTHB
    
    if (isDifferent) {
      setLocalChanges(prev => new Map(prev.set(expenseId, updatedLocal)))
    } else {
      // Remove from local changes if it's back to original
      setLocalChanges(prev => {
        const newMap = new Map(prev)
        newMap.delete(expenseId)
        return newMap
      })
    }
  }

  // Get current expense data (with local changes)
  const getCurrentExpenseData = (expense: IExpenseItem) => {
    const localChange = localChanges.get(expense.id)
    return {
      name: localChange?.name ?? expense.name,
      amount: localChange?.amount ?? expense.amountTHB
    }
  }

  // Handle manual update
  const handleUpdateExpenses = async () => {
    setErrorMessage('') // Clear previous errors
    
    if (!hasChanges) {
      setErrorMessage('ไม่มีการเปลี่ยนแปลงที่ต้องบันทึก')
      return
    }

    setUpdating(true)
    try {
      let allSuccess = true
      
      for (const [expenseId, localData] of localChanges.entries()) {
        const success = await onUpdateExpense(expenseId, localData.name, localData.amount)
        if (!success) {
          allSuccess = false
          break
        }
      }

      if (allSuccess) {
        setLocalChanges(new Map())
        setErrorMessage('')
        message.success('อัปเดตข้อมูลค่าใช้จ่ายสำเร็จ')
      } else {
        setErrorMessage('เกิดข้อผิดพลาดในการอัปเดตข้อมูล')
      }
    } catch (error) {
      console.error('Update error:', error)
      setErrorMessage('เกิดข้อผิดพลาดในการอัปเดตข้อมูล')
    } finally {
      setUpdating(false)
    }
  }

  // Handle add expense
  const handleAddExpense = async () => {
    setErrorMessage('') // Clear previous errors
    
    if (!newExpenseName.trim()) {
      setErrorMessage('กรุณากรอกชื่อรายการค่าใช้จ่าย')
      return
    }
    if (newExpenseAmount <= 0) {
      setErrorMessage('กรุณากรอกจำนวนเงินที่มากกว่า 0')
      return
    }

    setIsAddingNew(true)
    try {
      const success = await onAddExpense(newExpenseName.trim(), newExpenseAmount)
      if (success) {
        setNewExpenseName('')
        setNewExpenseAmount(0)
        setErrorMessage('')
        message.success('เพิ่มรายการค่าใช้จ่ายสำเร็จ')
      } else {
        setErrorMessage('ไม่สามารถเพิ่มรายการค่าใช้จ่ายได้')
      }
    } catch (error) {
      console.error('Error adding expense:', error)
      setErrorMessage('เกิดข้อผิดพลาดในการเพิ่มรายการค่าใช้จ่าย')
    } finally {
      setIsAddingNew(false)
    }
  }

  // Handle delete expense
  const handleDeleteExpense = async (expenseId: string) => {
    setErrorMessage('') // Clear previous errors
    
    const success = await onDeleteExpense(expenseId)
    if (success) {
      // Remove from local changes if exists
      setLocalChanges(prev => {
        const newMap = new Map(prev)
        newMap.delete(expenseId)
        return newMap
      })
      message.success('ลบรายการค่าใช้จ่ายสำเร็จ')
    } else {
      setErrorMessage('เกิดข้อผิดพลาดในการลบรายการ')
    }
  }

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: financialInputStyles }} />
      <Card
      title={
        <div className="flex items-center space-x-2">
          {typeInfo.icon}
          <span>{typeInfo.name}</span>
        </div>
      }
      loading={loading}
      className="h-full"
      extra={
        <Button
          type="primary"
          size="small"
          icon={<SaveOutlined />}
          onClick={handleUpdateExpenses}
          loading={updating}
          disabled={!hasChanges || loading}
        >
          Save
        </Button>
      }
    >
      <div className="space-y-3">
        {/* Error Message */}
        {errorMessage && (
          <div className="p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
            <strong>เกิดข้อผิดพลาด:</strong> {errorMessage}
          </div>
        )}
        

        {/* Existing Expenses */}
        {expenses.length > 0 && (
          <div className="space-y-2">
            {expenses.map((expense) => {
              const currentData = getCurrentExpenseData(expense)
              const hasLocalChanges = localChanges.has(expense.id)
              
              return (
                <div key={expense.id} className={`p-2 rounded border transition-all ${
                  hasLocalChanges ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white'
                }`}>
                  <Row gutter={[6, 8]} align="middle">
                    <Col xs={24} sm={12} md={12}>
                      <Input
                        value={currentData.name}
                        onChange={(e) => handleExpenseChange(expense.id, 'name', e.target.value)}
                        placeholder={typeInfo.placeholder}
                        disabled={loading || updating}
                        size="middle"
                        className="expense-text-input"
                      />
                    </Col>
                    <Col xs={16} sm={8} md={8}>
                      <InputNumber
                        value={currentData.amount === 0 ? null : currentData.amount}
                        onChange={(value) => handleExpenseChange(expense.id, 'amount', value || 0)}
                        placeholder="0"
                        min={0}
                        precision={2}
                        size="middle"
                        className="w-full financial-input"
                        addonBefore="฿"
                        formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        parser={(value) => parseFloat(value!.replace(/\$\s?|(,*)/g, '')) || 0}
                        disabled={loading || updating}
                        onKeyDown={(e) => {
                          // Allow only numbers, decimal point, and control keys
                          if (!/[0-9.]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                            e.preventDefault()
                          }
                        }}
                      />
                    </Col>
                    <Col xs={8} sm={4} md={4} className="text-right">
                      <Popconfirm
                        title="Delete Item"
                        description="Are you sure?"
                        onConfirm={() => handleDeleteExpense(expense.id)}
                        okText="Delete"
                        cancelText="Cancel"
                      >
                        <Button
                          size="middle"
                          danger
                          icon={<DeleteOutlined />}
                          disabled={loading || updating}
                          className="w-full"
                        />
                      </Popconfirm>
                    </Col>
                  </Row>
                </div>
              )
            })}
          </div>
        )}

        {/* Add New Expense */}
        <div className="border-t pt-3">
          <div className="text-xs text-gray-600 mb-2">เพิ่มรายการใหม่</div>
          <div className="space-y-2">
            <Row gutter={[6, 8]}>
              <Col xs={24} sm={12} md={12}>
                <Input
                  placeholder={typeInfo.placeholder}
                  value={newExpenseName}
                  onChange={(e) => {
                    setNewExpenseName(e.target.value)
                    if (errorMessage) setErrorMessage('') // Clear error when typing
                  }}
                  disabled={loading || isAddingNew}
                  size="middle"
                  className="expense-text-input"
                />
              </Col>
              <Col xs={16} sm={8} md={8}>
                <InputNumber
                  placeholder="จำนวนเงิน"
                  value={newExpenseAmount === 0 ? null : newExpenseAmount}
                  onChange={(value) => {
                    setNewExpenseAmount(value || 0)
                    if (errorMessage) setErrorMessage('') // Clear error when typing
                  }}
                  min={0}
                  precision={2}
                  className="w-full financial-input"
                  size="middle"
                  addonBefore="฿"
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => parseFloat(value!.replace(/\$\s?|(,*)/g, '')) || 0}
                  disabled={loading || isAddingNew}
                  onKeyDown={(e) => {
                    // Allow only numbers, decimal point, and control keys
                    if (!/[0-9.]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                      e.preventDefault()
                    }
                  }}
                />
              </Col>
              <Col xs={8} sm={4} md={4}>
                <Button
                  type="primary"
                  size="middle"
                  onClick={handleAddExpense}
                  loading={isAddingNew}
                  disabled={!newExpenseName.trim() || newExpenseAmount <= 0 || loading}
                  className="w-full"
                >
                  <span className="hidden sm:inline">+ Add</span>
                </Button>
              </Col>
            </Row>
          </div>
        </div>

        {/* Total Summary */}
        <div className="border-t pt-3">
          <div className="flex items-center justify-between p-3 bg-gray-100 rounded">
            <span className="font-semibold text-lg">รวม</span>
            <span className={`font-bold text-lg text-${typeInfo.color}-600`}>
              {formatCurrency(totalAmount)}
            </span>
          </div>
        </div>
      </div>
    </Card>
    </>
  )
}